<template>
  <div>
    <page-header title="Cobranças do fechamento">
      <template #description>
        Cada linha é a parte da Quero numa cobrança. A mesma cobrança aparece duas vezes quando
        tem crédito e estorno no mês. A soma das linhas é o valor da nota.
      </template>
    </page-header>

    <router-link :to="{ name: 'fee-closings', query: backQuery }" class="fee-closing__back">
      ‹ Voltar
    </router-link>

    <div v-if="closing" class="fee-closing">
      <h3 class="fee-closing__title">
        {{ formatCnpj(closing.tomador_document) }}
        <small>{{ closing.institution_names.join(', ') }} · {{ closing.month }}</small>
      </h3>
      <p class="fee-closing__total">
        {{ closing.items_count }} cobranças · {{ closing.total | formatCurrency }}
      </p>

      <p class="fee-closing__emission-actions">
        <button v-if="closing.can_emit" v-permissions:fee_closing.emit class="btn btn-primary fee-closing__emit"
                @click="emitClosing"
        >
          Emitir nota
        </button>
        <button v-if="closing.can_retry" v-permissions:fee_closing.emit class="btn btn-default fee-closing__retry"
                @click="retryEmission"
        >
          Gerar nova tentativa
        </button>
      </p>

      <table v-if="closing.emissions.length" class="table table-striped table-bordered fee-closing__emissions">
        <thead>
          <tr>
            <th>Tentativa</th>
            <th>Situação</th>
            <th>Referência</th>
            <th>NFS-e</th>
            <th>Motivo</th>
            <th>Pedida por</th>
            <th>Criada em</th>
            <th>Emitida em</th>
            <th>Scriba</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="emission in closing.emissions" :key="emission.id" class="fee-closing__emission-row">
            <td class="fee-closing__emission-attempt">
              {{ emission.attempt }}
            </td>
            <td class="fee-closing__emission-status">
              {{ emissionStatusLabel(emission.status) }}
            </td>
            <td class="fee-closing__emission-ref">
              {{ emission.emission_ref }}
            </td>
            <td class="fee-closing__emission-nfse">
              {{ emission.nfse_number }}
            </td>
            <td class="fee-closing__emission-rejection">
              {{ emission.rejection_message }}
            </td>
            <td class="fee-closing__emission-requested-by">
              {{ emission.requested_by }}
            </td>
            <td>{{ emission.created_at | formatDateTime }}</td>
            <td>{{ emission.issued_at | formatDateTime }}</td>
            <td>
              <a v-if="emission.scriba_url" :href="emission.scriba_url" target="_blank"
                 class="fee-closing__emission-scriba-link"
              >
                Ver no Scriba
              </a>
            </td>
          </tr>
        </tbody>
      </table>

      <table class="table table-striped table-bordered fee-closing__items">
        <thead>
          <tr>
            <th>Cobrança</th>
            <th>Tipo</th>
            <th class="text-right">
              Parcela
            </th>
            <th>Liberado em</th>
            <th class="text-right">
              Valor
            </th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="item in closing.items" :key="item.id" :class="{ warning: needsAttention(item) }"
              class="fee-closing__item"
          >
            <td>{{ item.charge_reference }}</td>
            <td>
              {{ payableType(item) }}
              <span v-if="item.payable_status === 'waiting_funds'" class="label label-warning">
                aguardando liberação
              </span>
            </td>
            <td class="text-right">
              {{ item.installment }}
            </td>
            <td>{{ item.funds_available_at | formatDate }}</td>
            <td class="text-right">
              {{ item.amount | formatCurrency }}
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>
</template>

<script>
import FeeClosingsService from 'services/FeeClosingsService';
import Permissions from 'mixins/Permissions';
import { PAYABLE_TYPES, EMISSION_STATUSES, formatCnpj } from './labels';

export default {
  mixins: [Permissions],
  data() {
    return {
      closing: null,
    };
  },
  computed: {
    backQuery() {
      return this.closing ? { month: this.closing.month } : {};
    },
  },
  mounted() {
    this.loadClosing();
  },
  methods: {
    formatCnpj,

    payableType(item) {
      return PAYABLE_TYPES[item.payable_type] || item.payable_type;
    },

    emissionStatusLabel(status) {
      return EMISSION_STATUSES[status];
    },

    // Chargeback e parcela sem liberação confirmada são os casos que o fiscal ainda vai decidir.
    needsAttention(item) {
      return item.payable_type === 'chargeback' || item.payable_status === 'waiting_funds';
    },

    loadClosing() {
      this.$loader.show();
      return FeeClosingsService.get(this.$route.params.id)
        .then((closing) => {
          this.closing = closing;
          this.$loader.hide();
        })
        .catch((error) => {
          this.$loader.hide();
          this.$alert.notifyAlert(error.response?.data?.message || 'Erro ao carregar as cobranças do fechamento.');
        });
    },

    emitClosing() {
      FeeClosingsService.emitClosing(this.closing.id)
        .then(() => this.loadClosing())
        .catch((error) => {
          this.$alert.notifyAlert(error.response?.data?.message || 'Erro ao emitir a nota.');
        });
    },

    retryEmission() {
      FeeClosingsService.retryEmission(this.closing.id)
        .then(() => this.loadClosing())
        .catch((error) => {
          this.$alert.notifyAlert(error.response?.data?.message || 'Erro ao pedir a nova tentativa.');
        });
    },
  },
};
</script>

<style scoped>
  .fee-closing__back {
    display: inline-block;
    margin-bottom: 15px;
  }
</style>
